import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ChevronDownIcon, CalendarIcon, SendIcon } from 'lucide-react';
import { RentalFormData, FullReservationData } from '../App';
interface ReservationPageProps {
  onNavigate: (page: string) => void;
  initialFormData: RentalFormData | null;
  onSubmitFullReservation: (data: FullReservationData) => void;
}
export function ReservationPage({
  onNavigate,
  initialFormData,
  onSubmitFullReservation
}: ReservationPageProps) {
  const rentalTypes = ['Residential', 'Hotel', 'Airbnb', 'Baltimore Ports', 'Seaports', 'Others'];
  const deliveryLocations = ['Washington DC', 'Maryland', 'Baltimore', 'Northern Virginia'];
  const equipmentOptions = ['Rollator Walker', 'Power Wheelchair', 'Knee Walker', 'Manual Wheelchair', '3 or 4 Wheel Scooter (Regular)', '3 or 4 Wheel Scooter (Heavy Duty)', 'Heavy Duty Manual Wheelchair', 'Baby Stroller'];
  const gelCushionOptions = ['None', 'Standard Gel Cushion', 'Premium Gel Cushion', 'Memory Foam Cushion'];
  const [formData, setFormData] = useState<FullReservationData>({
    rentalType: initialFormData?.rentalType || 'Residential',
    placeName: initialFormData?.placeName || '',
    placeAddress: initialFormData?.placeAddress || '',
    deliveryLocation: initialFormData?.deliveryLocation || '',
    equipment: initialFormData?.equipment || '',
    rentalDate: initialFormData?.rentalDate || '',
    returnDate: initialFormData?.returnDate || '',
    weight: initialFormData?.weight || '',
    height: initialFormData?.height || '',
    price: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: '',
    gelCushion: ''
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmitFullReservation(formData);
  };
  const inputClass = 'w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none';
  const labelClass = 'block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider';
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-1325">
      <PageHeader title="Complete Your Reservation" breadcrumb="Reservation" onNavigateHome={() => onNavigate('home')} data-id="element-1326" />

      <section className="py-20" data-id="element-1327">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1328">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="bg-brand-navy rounded-[2rem] shadow-2xl p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-1329">
            {/* Decorative */}
            <div className="absolute -top-32 -right-32 w-80 h-80 bg-brand-blue/10 rounded-full blur-3xl pointer-events-none" data-id="element-1330"></div>
            <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-brand-red/10 rounded-full blur-3xl pointer-events-none" data-id="element-1331"></div>

            <div className="relative z-10" data-id="element-1332">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-1333">
                Reservation Details
              </h2>
              <p className="text-white/60 mb-10" data-id="element-1334">
                Review your information and provide additional details to
                complete your booking.
              </p>

              {initialFormData && <div className="bg-white/5 border border-white/10 rounded-xl px-5 py-3 mb-10 inline-flex items-center gap-2" data-id="element-1335">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-1336"></div>
                  <span className="text-green-400/90 text-sm font-medium" data-id="element-1337">
                    Form prefilled from your previous selection
                  </span>
                </div>}

              <form onSubmit={handleSubmit} className="space-y-10" data-id="element-1338">
                {/* Section: Rental Info (Prefilled) */}
                <div data-id="element-1339">
                  <h3 className="text-lg font-bold text-white/90 mb-6 flex items-center gap-2" data-id="element-1340">
                    <span className="w-7 h-7 rounded-full bg-brand-red text-white text-xs font-bold flex items-center justify-center" data-id="element-1341">
                      1
                    </span>
                    Rental Information
                  </h3>

                  <div className="space-y-6" data-id="element-1342">
                    {/* Rental Type */}
                    <div data-id="element-1343">
                      <label className={labelClass} data-id="element-1344">Type of Rental</label>
                      <div className="flex flex-wrap gap-3" data-id="element-1345">
                        {rentalTypes.map(type => <button key={type} type="button" onClick={() => setFormData({
                        ...formData,
                        rentalType: type
                      })} className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${formData.rentalType === type ? 'bg-brand-red text-white shadow-lg shadow-brand-red/20' : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'}`} data-id="element-1346">
                            {type}
                          </button>)}
                      </div>
                    </div>

                    {/* Place Name & Address */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1347">
                      <div data-id="element-1348">
                        <label htmlFor="placeName" className={labelClass} data-id="element-1349">
                          Place Name
                        </label>
                        <input type="text" id="placeName" name="placeName" value={formData.placeName} onChange={handleInputChange} placeholder="Enter place name" required className={inputClass} data-id="element-1350" />
                      </div>
                      <div data-id="element-1351">
                        <label htmlFor="placeAddress" className={labelClass} data-id="element-1352">
                          Place Address
                        </label>
                        <input type="text" id="placeAddress" name="placeAddress" value={formData.placeAddress} onChange={handleInputChange} placeholder="Enter place address" required className={inputClass} data-id="element-1353" />
                      </div>
                    </div>

                    {/* Delivery Location & Equipment */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1354">
                      <div data-id="element-1355">
                        <label htmlFor="deliveryLocation" className={labelClass} data-id="element-1356">
                          Delivery Location
                        </label>
                        <div className="relative" data-id="element-1357">
                          <select id="deliveryLocation" name="deliveryLocation" value={formData.deliveryLocation} onChange={handleInputChange} required className={`${inputClass} appearance-none`} data-id="element-1358">
                            <option value="" disabled className="text-gray-900" data-id="element-1359">
                              Select One
                            </option>
                            {deliveryLocations.map(loc => <option key={loc} value={loc} className="text-gray-900" data-id="element-1360">
                                {loc}
                              </option>)}
                          </select>
                          <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-1361" />
                        </div>
                      </div>
                      <div data-id="element-1362">
                        <label htmlFor="equipment" className={labelClass} data-id="element-1363">
                          Equipment
                        </label>
                        <div className="relative" data-id="element-1364">
                          <select id="equipment" name="equipment" value={formData.equipment} onChange={handleInputChange} required className={`${inputClass} appearance-none`} data-id="element-1365">
                            <option value="" disabled className="text-gray-900" data-id="element-1366">
                              Select One
                            </option>
                            {equipmentOptions.map(eq => <option key={eq} value={eq} className="text-gray-900" data-id="element-1367">
                                {eq}
                              </option>)}
                          </select>
                          <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-1368" />
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1369">
                      <div data-id="element-1370">
                        <label htmlFor="rentalDate" className={labelClass} data-id="element-1371">
                          Rental Date
                        </label>
                        <div className="relative" data-id="element-1372">
                          <input type="date" id="rentalDate" name="rentalDate" value={formData.rentalDate} onChange={handleInputChange} required className={`${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} data-id="element-1373" />
                          <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-1374" />
                        </div>
                      </div>
                      <div data-id="element-1375">
                        <label htmlFor="returnDate" className={labelClass} data-id="element-1376">
                          Return Date
                        </label>
                        <div className="relative" data-id="element-1377">
                          <input type="date" id="returnDate" name="returnDate" value={formData.returnDate} onChange={handleInputChange} required className={`${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} data-id="element-1378" />
                          <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-1379" />
                        </div>
                      </div>
                    </div>

                    {/* Weight & Height */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1380">
                      <div data-id="element-1381">
                        <label htmlFor="weight" className={labelClass} data-id="element-1382">
                          Weight
                        </label>
                        <input type="text" id="weight" name="weight" value={formData.weight} onChange={handleInputChange} placeholder="Weight in Pounds" required className={inputClass} data-id="element-1383" />
                      </div>
                      <div data-id="element-1384">
                        <label htmlFor="height" className={labelClass} data-id="element-1385">
                          Height
                        </label>
                        <input type="text" id="height" name="height" value={formData.height} onChange={handleInputChange} placeholder="Height in ft/cm" required className={inputClass} data-id="element-1386" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10" data-id="element-1387"></div>

                {/* Section: Additional Details (New fields) */}
                <div data-id="element-1388">
                  <h3 className="text-lg font-bold text-white/90 mb-6 flex items-center gap-2" data-id="element-1389">
                    <span className="w-7 h-7 rounded-full bg-brand-red text-white text-xs font-bold flex items-center justify-center" data-id="element-1390">
                      2
                    </span>
                    Your Details
                  </h3>

                  <div className="space-y-6" data-id="element-1391">
                    {/* Price & First Name */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1392">
                      <div data-id="element-1393">
                        <label htmlFor="price" className={labelClass} data-id="element-1394">
                          Price
                        </label>
                        <input type="text" id="price" name="price" value={formData.price} onChange={handleInputChange} placeholder="e.g. $150" className={inputClass} data-id="element-1395" />
                      </div>
                      <div data-id="element-1396">
                        <label htmlFor="firstName" className={labelClass} data-id="element-1397">
                          First Name
                        </label>
                        <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="First name" required className={inputClass} data-id="element-1398" />
                      </div>
                    </div>

                    {/* Last Name & Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1399">
                      <div data-id="element-1400">
                        <label htmlFor="lastName" className={labelClass} data-id="element-1401">
                          Last Name
                        </label>
                        <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Last name" required className={inputClass} data-id="element-1402" />
                      </div>
                      <div data-id="element-1403">
                        <label htmlFor="email" className={labelClass} data-id="element-1404">
                          Email
                        </label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="e.g. email@domain.com" required className={inputClass} data-id="element-1405" />
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1406">
                      <div data-id="element-1407">
                        <label htmlFor="phoneNumber" className={labelClass} data-id="element-1408">
                          Phone Number
                        </label>
                        <input type="tel" id="phoneNumber" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} placeholder="e.g. +123456789" required className={inputClass} data-id="element-1409" />
                      </div>
                      <div data-id="element-1410">
                        <label htmlFor="gelCushion" className={labelClass} data-id="element-1411">
                          Gel Cushion
                        </label>
                        <div className="relative" data-id="element-1412">
                          <select id="gelCushion" name="gelCushion" value={formData.gelCushion} onChange={handleInputChange} className={`${inputClass} appearance-none`} data-id="element-1413">
                            <option value="" disabled className="text-gray-900" data-id="element-1414">
                              Select One
                            </option>
                            {gelCushionOptions.map(opt => <option key={opt} value={opt} className="text-gray-900" data-id="element-1415">
                                {opt}
                              </option>)}
                          </select>
                          <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-1416" />
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    <div data-id="element-1417">
                      <label htmlFor="message" className={labelClass} data-id="element-1418">
                        Message (Optional)
                      </label>
                      <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} placeholder="Additional message or special requests" rows={4} className={`${inputClass} resize-none`} data-id="element-1419" />
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-4" data-id="element-1420">
                  <button type="submit" className="w-full py-4 bg-brand-red text-white rounded-xl font-bold text-lg hover:bg-red-600 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2 focus:ring-offset-brand-navy flex items-center justify-center gap-3" data-id="element-1421">
                    <SendIcon className="w-5 h-5" data-id="element-1422" />
                    Submit Reservation
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}